import { useCallback, useState } from "react";

import axios from "axios";

import {
  acquireCSRFToken,
  defaultApiUrl,
  API_VERSION,
} from "../../../libraries/network.implementation";

export interface TransactionReceiptUploadResponse {
  uuid: string;
  status: string;
  original_filename?: string;
}

interface UseTransactionReceiptUploadResult {
  uploadingTransactionIds: ReadonlySet<string>;
  upload: (
    transactionId: string,
    file: File
  ) => Promise<TransactionReceiptUploadResponse>;
  remove: (transactionId: string, deleteReceipt?: boolean) => Promise<void>;
}

const receiptUrl = (transactionId: string) =>
  `${defaultApiUrl}/${API_VERSION}/transactions/${transactionId}/receipt`;

const useTransactionReceiptUpload = (): UseTransactionReceiptUploadResult => {
  // A set, not a single id: rows upload independently and the first request to
  // finish would otherwise clear a sibling's spinner while it is still running.
  const [uploadingTransactionIds, setUploadingTransactionIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const upload = useCallback(async (transactionId: string, file: File) => {
    setUploadingTransactionIds((ids) => new Set(ids).add(transactionId));
    const formData = new FormData();
    formData.append("file", file);
    try {
      // Django rejects the POST without an x-csrftoken header. The shared
      // network wrapper adds it for JSON requests, but multipart bypasses that
      // wrapper, so the token is acquired directly here. Skipping this produces
      // a 403 before any permission check runs.
      const csrfToken = await acquireCSRFToken();
      const result = await axios.post<TransactionReceiptUploadResponse>(
        receiptUrl(transactionId),
        formData,
        {
          // Browser sets the multipart boundary itself.
          headers: { Accept: "application/json", "x-csrftoken": csrfToken },
          withCredentials: true,
        }
      );
      return result.data;
    } finally {
      setUploadingTransactionIds((ids) => {
        const next = new Set(ids);
        next.delete(transactionId);
        return next;
      });
    }
  }, []);

  const remove = useCallback(
    async (transactionId: string, deleteReceipt = false) => {
      const csrfToken = await acquireCSRFToken();
      const headers = { Accept: "application/json", "x-csrftoken": csrfToken };
      // Unlink (the default) and delete are different operations on different
      // routes now, not one DELETE branching on a query flag.
      if (deleteReceipt) {
        await axios.delete(receiptUrl(transactionId), {
          headers,
          withCredentials: true,
        });
      } else {
        await axios.post(`${receiptUrl(transactionId)}/unlink`, undefined, {
          headers,
          withCredentials: true,
        });
      }
    },
    []
  );

  return { uploadingTransactionIds, upload, remove };
};

export default useTransactionReceiptUpload;
